
import { UrlParams } from "./urlparams";
import ClickData from "./clickdata";
import SessionStore from "./storesession";
import CookieStore from "./storecookie";
import { GoogleAnalytics } from "./googleanalytics";
import Cacheable from "./cacheable";
import { MapCookie, PardotTypeEnum } from "./mapcookie";
import Referrer from "./referrer";
import { VERSION } from "./version";
import ChannelGrouping from "./channelgrouping";
import Cookies from "js-cookie";

export enum SessionProfileAttribute {

    // Google Analytics Data
    clientId = 'clientId',
    sessionId = 'sessionId',
    hitId = 'hitId',

    // Source Data
    referrer = 'referrer',
    channelGrouping = 'channelGrouping',

    // UTMs
    utmCampaignId = 'utmCampaignId',
    utmCampaignSource = 'utmCampaignSource',
    utmCampaignMedium = 'utmCampaignMedium',
    utmCampaignName = 'utmCampaignName',
    utmCampaignTerm = 'utmCampaignTerm',
    utmCampaignContent = 'utmCampaignContent',

    // Ads Metadata
    clickId = 'clickId',
    clickPlatform = 'clickPlatform',
    gclid = 'gclid',
    gbraid = 'gbraid',
    wbraid = 'wbraid',
    dclid = 'dclid',
    msclkid = 'msclkid',
    fbclid = 'fbclid',
    kcid = 'kcid',

    // HTTP Client metadata
    userAgent = 'userAgent',
    browserVendor = 'browserVendor',
    browserLanguage = 'browserLanguage',

    // MAP cookies
    activeDemandSessionGuidCookie = 'activeDemandSessionGuidCookie',
    actonWebsiteProspectorCookieKeys = 'actonWebsiteProspectorCookieKeys',
    actonWebsiteProspectorCookieValues = 'actonWebsiteProspectorCookieValues',
    hubspotContactCookie = 'hubspotContactCookie',
    pardotVisitorIdCookieKeys = 'pardotVisitorIdCookieKeys',
    pardotVisitorIdCookieValues = 'pardotVisitorIdCookieValues',
    pardotVisitorHashCookieKeys = 'pardotVisitorHashCookieKeys',
    pardotVisitorHashCookieValues = 'pardotVisitorHashCookieValues',

    // Lead Source data
    leadSources = 'leadSources',
    leadSourceCategories = 'leadSourceCategories',

    // Diagnostic Data
    diagnosticCookiesAllowed = 'diagnosticCookiesAllowed',
    diagnosticSessionStorageAllowed = 'diagnosticSessionStorageAllowed',
    diagnosticVersion = 'diagnosticVersion',

    // All Values
    allValues = 'allValues',

    // Lead Classification Attributes
    leadIsUnpaidOrBrandedPaid = 'leadIsUnpaidOrBrandedPaid',
    leadIsUnpaid = 'leadIsUnpaid',

}

export interface SessionProfileQuery {
    attribute: SessionProfileAttribute;
    index: number;
}


export interface SessionProfile {

    // Google Analytics Data
    [SessionProfileAttribute.clientId]: Array<string>;
    [SessionProfileAttribute.sessionId]: Array<string>;
    [SessionProfileAttribute.hitId]: Array<string>;

    // Source Data
    [SessionProfileAttribute.referrer]: Array<string>;
    [SessionProfileAttribute.channelGrouping]: Array<string>;

    // UTMs
    [SessionProfileAttribute.utmCampaignId]: Array<string>;
    [SessionProfileAttribute.utmCampaignSource]: Array<string>;
    [SessionProfileAttribute.utmCampaignMedium]: Array<string>;
    [SessionProfileAttribute.utmCampaignName]: Array<string>;
    [SessionProfileAttribute.utmCampaignTerm]: Array<string>;
    [SessionProfileAttribute.utmCampaignContent]: Array<string>;

    // Ads Metadata
    [SessionProfileAttribute.clickId] : Array<string>;
    [SessionProfileAttribute.clickPlatform]: Array<string>;
    [SessionProfileAttribute.gclid] : Array<string>;
    [SessionProfileAttribute.gbraid] : Array<string>;
    [SessionProfileAttribute.wbraid] : Array<string>;
    [SessionProfileAttribute.dclid] : Array<string>;
    [SessionProfileAttribute.msclkid] : Array<string>;
    [SessionProfileAttribute.fbclid] : Array<string>;
    [SessionProfileAttribute.kcid] : Array<string>;

    // HTTP Client metadata
    [SessionProfileAttribute.userAgent]: Array<string>;
    [SessionProfileAttribute.browserVendor]: Array<string>;
    [SessionProfileAttribute.browserLanguage]: Array<string>;

    // MAP cookies
    [SessionProfileAttribute.activeDemandSessionGuidCookie]: Array<string>;
    [SessionProfileAttribute.actonWebsiteProspectorCookieKeys]: Array<string>;
    [SessionProfileAttribute.actonWebsiteProspectorCookieValues]: Array<string>;
    [SessionProfileAttribute.hubspotContactCookie]: Array<string>;
    [SessionProfileAttribute.pardotVisitorIdCookieKeys] : Array<string>;
    [SessionProfileAttribute.pardotVisitorIdCookieValues] : Array<string>;
    [SessionProfileAttribute.pardotVisitorHashCookieKeys] : Array<string>;
    [SessionProfileAttribute.pardotVisitorHashCookieValues] : Array<string>;

    // Lead source data
    [SessionProfileAttribute.leadSources]: Array<string>;
    [SessionProfileAttribute.leadSourceCategories]: Array<string>;

    // Diagnostic Data
    [SessionProfileAttribute.diagnosticCookiesAllowed]: Array<boolean>;
    [SessionProfileAttribute.diagnosticSessionStorageAllowed]: Array<boolean>;
    [SessionProfileAttribute.diagnosticVersion]: Array<string>;

    // All Values is not an attribute of session profile
    // but a pointer to the entire profile's contents
    // (special field)

    // Lead Classification Attributes
    [SessionProfileAttribute.leadIsUnpaid]: Array<number>;
    [SessionProfileAttribute.leadIsUnpaidOrBrandedPaid]: Array<number>;
}

export interface SessionProfileQueryBinding {
    element: HTMLInputElement;
    query: SessionProfileQuery;
}

export class Loader 
{
    public static execute() : SessionProfile
    {
        const cache = new Cacheable();
        const params = UrlParams.parse();
        const clickData = ClickData.parse(params);
        const sessionStore = new SessionStore();
        const cookieStore = new CookieStore();

        const gclid = Loader.findOrCalculate(cache, SessionProfileAttribute.gclid, UrlParams.get('gclid', params));
        const gbraid = Loader.findOrCalculate(cache, SessionProfileAttribute.gbraid, UrlParams.get('gbraid', params));
        const wbraid = Loader.findOrCalculate(cache, SessionProfileAttribute.wbraid, UrlParams.get('wbraid', params));
        const dclid = Loader.findOrCalculate(cache, SessionProfileAttribute.dclid, UrlParams.get('dclid', params));
        const msclkid = Loader.findOrCalculate(cache, SessionProfileAttribute.msclkid, UrlParams.get('msclkid', params));
        const fbclid = Loader.findOrCalculate(cache, SessionProfileAttribute.fbclid, UrlParams.get('fbclid', params));
        const kcid = Loader.findOrCalculate(cache, SessionProfileAttribute.kcid, UrlParams.get('kcid', params));

        let originalSource = Loader.findOrCalculate(cache, SessionProfileAttribute.utmCampaignSource, UrlParams.get('utm_source', params));
        let originalMedium = Loader.findOrCalculate(cache, SessionProfileAttribute.utmCampaignMedium, UrlParams.get('utm_medium', params));
        let source: Array<string> = [];
        let medium: Array<string> = [];
        // calculate commonly associated source / medium
        // based on click identifier / platform
        if (gclid.length || gbraid.length || wbraid.length) {
            source = ['google'];
            medium = ['cpc'];
        } else if (dclid.length) {
            source = ['google'];
            medium = ['display'];
        } else if (msclkid.length) {
            source = ['bing'];
            medium = ['cpc'];
        } else if (fbclid.length) {
            source = ['meta'];
            medium = ['paid-social'];
        }
        // respect the original source/medium if provided
        if (originalSource.length) {
            source = originalSource;
        }
        if (originalMedium.length) {
            medium = originalMedium;
        }

        // calculate unpaid lead
        const channelGrouping = Loader.findOrCalculate(cache, SessionProfileAttribute.channelGrouping, [ChannelGrouping.get(params)]);
        let unpaidLead = Loader.calculateUnpaidLead(channelGrouping);
        
        // calculate unpaid and branded
        const campaignName = Loader.findOrCalculate(cache, SessionProfileAttribute.utmCampaignName, UrlParams.getOneOf(['utm_campaign', 'campaign'], params));
        let unpaidOrBrandedLead = Loader.calculateUnpaidOrBrandedPaidLead(campaignName, unpaidLead);

        return {
            // Google Analytics Data
            [SessionProfileAttribute.clientId]: Loader.findOrCalculate(cache, SessionProfileAttribute.clientId, GoogleAnalytics.getClientIds()),
            [SessionProfileAttribute.sessionId]: Loader.findOrCalculate(cache, SessionProfileAttribute.sessionId, GoogleAnalytics.getSessionIds()),
            [SessionProfileAttribute.hitId]: GoogleAnalytics.getHitIds(),  // not cached!
        
            // Source Data - Always one of each so wrap in array for API consistency
            [SessionProfileAttribute.referrer]: Loader.findOrCalculate(cache, SessionProfileAttribute.referrer, Referrer.get()),
            [SessionProfileAttribute.channelGrouping]: channelGrouping,

            // UTMs
            [SessionProfileAttribute.utmCampaignId]: Loader.findOrCalculate(cache, SessionProfileAttribute.utmCampaignId, UrlParams.get('utm_id', params)),
            [SessionProfileAttribute.utmCampaignSource]: source,
            [SessionProfileAttribute.utmCampaignMedium]: medium,
            [SessionProfileAttribute.utmCampaignName]: campaignName,
            [SessionProfileAttribute.utmCampaignTerm]: Loader.findOrCalculate(cache, SessionProfileAttribute.utmCampaignTerm, UrlParams.getOneOf(['utm_term','keyword'], params)),
            [SessionProfileAttribute.utmCampaignContent]: Loader.findOrCalculate(cache, SessionProfileAttribute.utmCampaignContent, UrlParams.get('utm_content', params)),
        
            // Ads Metadata
            [SessionProfileAttribute.clickId] : Loader.findOrCalculate(cache, SessionProfileAttribute.clickId, clickData.id),
            [SessionProfileAttribute.clickPlatform]: Loader.findOrCalculate(cache, SessionProfileAttribute.clickPlatform, clickData.platform),
            [SessionProfileAttribute.gclid] : gclid,
            [SessionProfileAttribute.gbraid] : gbraid,
            [SessionProfileAttribute.wbraid] : wbraid,
            [SessionProfileAttribute.dclid] : dclid,
            [SessionProfileAttribute.msclkid] : msclkid,
            [SessionProfileAttribute.fbclid] : fbclid,
            [SessionProfileAttribute.kcid] : kcid,

            // HTTP Client metadata - naturally persistent values so not cached
            [SessionProfileAttribute.userAgent]: [String(window.navigator.userAgent ? window.navigator.userAgent : '')],
            [SessionProfileAttribute.browserVendor]: [String(window.navigator.vendor ? window.navigator.vendor : '')],
            [SessionProfileAttribute.browserLanguage]: [String(window.navigator.language ? window.navigator.language : '')],
        
            // MAP cookies - naturally persistent values so not cached
            [SessionProfileAttribute.activeDemandSessionGuidCookie]: MapCookie.getActiveDemand(cookieStore),
            [SessionProfileAttribute.actonWebsiteProspectorCookieKeys]: MapCookie.getActOn(cookieStore, false),
            [SessionProfileAttribute.actonWebsiteProspectorCookieValues]: MapCookie.getActOn(cookieStore, true),
            [SessionProfileAttribute.hubspotContactCookie]: MapCookie.getHubSpot(cookieStore),
            [SessionProfileAttribute.pardotVisitorIdCookieKeys] : MapCookie.getPardotVisitor(cookieStore, PardotTypeEnum.VisitorIdKey),
            [SessionProfileAttribute.pardotVisitorIdCookieValues] : MapCookie.getPardotVisitor(cookieStore, PardotTypeEnum.VisitorIdValue),
            [SessionProfileAttribute.pardotVisitorHashCookieKeys] : MapCookie.getPardotVisitor(cookieStore, PardotTypeEnum.VisitorHashKey),
            [SessionProfileAttribute.pardotVisitorHashCookieValues] : MapCookie.getPardotVisitor(cookieStore, PardotTypeEnum.VisitorHashValue),
        
            // Lead source data - always calculated fresh so not cached
            [SessionProfileAttribute.leadSources]: [],
            [SessionProfileAttribute.leadSourceCategories]: [],

            // Diagnostic Data - naturally persistent values so not cached
            [SessionProfileAttribute.diagnosticCookiesAllowed]: [cookieStore.status],
            [SessionProfileAttribute.diagnosticSessionStorageAllowed]: [sessionStore.status],
            [SessionProfileAttribute.diagnosticVersion]: [VERSION],

            // Lead Classification - always calculated fresh
            [SessionProfileAttribute.leadIsUnpaid]: unpaidLead,
            [SessionProfileAttribute.leadIsUnpaidOrBrandedPaid]: unpaidOrBrandedLead,

        }
    }

    static calculateUnpaidLead(channelGrouping: Array<string>) : Array<number>
    {
        let unpaidLead = [];
        if (channelGrouping.length > 0) {
            for (let i = 0; i < channelGrouping.length; i ++) {
                // evaluate the current channel against the paid channels
                const isPaid = ['Paid Search', 'Display', 'Paid Social'].indexOf(channelGrouping[i]) === -1;
                unpaidLead.push(Number(isPaid));
            }
        }
        return unpaidLead;
    }

    static calculateUnpaidOrBrandedPaidLead(campaignName : Array<string>, unpaidLead: Array<number>) : Array<number>
    {
        let unpaidOrBrandedLead = [];
        if (unpaidLead.length > 0) {
            for (let i = 0; i < unpaidLead.length; i ++) {
                let isUnpaidLeadOrBrandedLead = unpaidLead[i];
                if (!isUnpaidLeadOrBrandedLead) {
                    if (campaignName.length > i) {
                        const campaignNameMember = campaignName[i];
                        const isBrandedCampaign = campaignNameMember.toLocaleLowerCase().indexOf('brand') !== -1;
                        if (isBrandedCampaign) {
                            isUnpaidLeadOrBrandedLead = 1;
                        }
                    }
                }
                unpaidOrBrandedLead.push(isUnpaidLeadOrBrandedLead);
            }
        }
        return unpaidOrBrandedLead;
    }

    static findOrCalculate(cache: Cacheable, key: string, value: Array<string>) {
        return cache.get(`fountain-analytics-${key}`, () => {return value});
    }

}
